import { Fragment, useState } from "react";
import Sidebar from "./components/sidebar";
import CommandPalette from "./components/CommandPalette";
import CompanySwitcher from "./components/CompanySwitcher";
import { Search } from "lucide-react";
import Overview from "./components/Overview";
import Banks from "./components/Banks";
import Brands from "./components/Brands";
import Products from "./components/Products";
import TaxRates from "./components/TaxRates";
import ProductCategories from "./components/ProductCategories";
import PriceLists from "./components/PriceLists";
import Customers from "./components/Customers";
import Beats from "./components/Beats";
import SalesmanBeats from "./components/SalesmanBeats";
import HeaderOnlyInvoiceImport from "./components/HeaderOnlyInvoiceImport";
import CustomerBeats from "./components/CustomerBeats";
import PurchaseInvoices from "./components/PurchaseInvoices";
import PurchaseDebitNotes from "./components/PurchaseDebitNotes";
import SalesOrders from "./components/SalesOrders";
import SalesInvoices from "./components/SalesInvoices";
import Schemes from "./components/Schemes";
import CreditDebitNotes from "./components/CreditDebitNotes";
import PaymentReceipts from "./components/PaymentReceipts";
import CashDeposits from "./components/CashDeposits";
import VoucherTypes from "./components/VoucherTypes";
import StockBalances from "./components/StockBalances";
import StockLedger from "./components/StockLedger";
import Warehouses from "./components/Warehouses";
import LoadingSheets from "./components/LoadingSheets";
import Users from "./components/Users";
import Distributors from "./components/Distributors";
import Roles from "./components/Roles";
import Firms from "./components/Firms";
import DistributorProfile from "./components/DistributorProfile";
import ImportColumnMapping from "./components/ImportColumnMapping";
import ReportTable from "./components/reports/ReportTable";
import GstReport from "./components/reports/GstReport";
import ClosingStockReport from "./components/reports/ClosingStockReport";
import CategoryClosingStockReport from "./components/reports/CategoryClosingStockReport";
import ItemWiseSalesReport from "./components/reports/ItemWiseSalesReport";
import OutstandingBillsReport from "./components/reports/OutstandingBillsReport";
import LedgerStatementReport from "./components/reports/LedgerStatementReport";
import CashInHandReport from "./components/reports/CashInHandReport";
import Ledgers from "./components/accounting/Ledgers";
import Vouchers from "./components/accounting/Vouchers";
import AccountingSettings from "./components/accounting/AccountingSettings";
import FinancialSummary from "./components/accounting/FinancialSummary";
import ProfitAndLoss from "./components/accounting/ProfitAndLoss";
import ExpenseReport from "./components/accounting/ExpenseReport";
import BalanceSheet from "./components/accounting/BalanceSheet";
import LedgerWiseTotals from "./components/accounting/LedgerWiseTotals";
import LedgerVouchers from "./components/accounting/LedgerVouchers";
import CashBankBalances from "./components/accounting/CashBankBalances";
import CashFlow from "./components/accounting/CashFlow";
import WorkingCapital from "./components/accounting/WorkingCapital";
import RatioAnalysis from "./components/accounting/RatioAnalysis";
import StockValuation from "./components/accounting/StockValuation";
import OutstandingAgeing from "./components/accounting/OutstandingAgeing";
import { getViewCompanyId } from "./services/api";
import Alerts from "./components/Alerts";
import AlertsMarquee from "./components/AlertsMarquee";
import StockTransfers from "./components/StockTransfers";
import ImportHistory from "./components/ImportHistory";
import DistributorLinks from "./components/DistributorLinks";
import StockJournalReport from "./components/reports/StockJournalReport";
import ClosingStockHistory from "./components/reports/ClosingStockHistory";
import { isReceivablePage, receivablesFeatureMeta, renderReceivablePage } from "./components/receivables";
import {
  stockSummaryConfig,
  stockMovementConfig,
  salesRegisterConfig,
  purchaseRegisterConfig,
  customerWiseSalesConfig,
  salesmanWiseSalesConfig,
  creditNotesConfig,
  billWisePaymentsConfig,
  productWiseReturnsConfig,
  schemeDiscountConfig,
  stockLowConfig,
  stockExcessConfig,
} from "./components/reports/reportConfigs";

const featureMeta = {
  overview: { label: "Overview", crumb: "Dashboard" },
  alerts: { label: "Alerts", crumb: "Dashboard" },
  stockTransfers: { label: "Stock Transfer (In / Out)", crumb: "Inventory" },
  stockJournalReport: { label: "Stock Journal", crumb: "Inventory" },
  stockLowReport: { label: "Stock Low", crumb: "Inventory" },
  stockExcessReport: { label: "Stock Excess", crumb: "Inventory" },
  closingStockHistory: { label: "Daily Closing Stock", crumb: "Inventory" },
  productWiseReturnsReport: { label: "Product Wise Return / Cancel", crumb: "Accounting Reports" },
  schemeDiscountReport: { label: "Scheme Discount", crumb: "Accounting Reports" },
  importHistory: { label: "Import Sync History", crumb: "Admin" },
  distributorLinks: { label: "Distributor Links", crumb: "Admin" },
  brands: { label: "Brands", crumb: "Masters" },
  banks: { label: "Banks", crumb: "Masters" },
  products: { label: "Products", crumb: "Masters" },
  taxRates: { label: "Tax Rates", crumb: "Masters" },
  productCategories: { label: "Product Categories", crumb: "Masters" },
  priceLists: { label: "Price Lists", crumb: "Masters" },
  customers: { label: "Customers", crumb: "Masters" },
  beats: { label: "Beats", crumb: "Masters" },
  salesmanBeats: { label: "Salesman Beats", crumb: "Masters" },
  headerOnlyInvoices: { label: "Import Invoices Without Items", crumb: "Sales" },
  customerBeats: { label: "Customer Beats", crumb: "Masters" },
  firms: { label: "Firms", crumb: "Masters" },
  purchaseInvoices: { label: "Purchase Invoices", crumb: "Purchases" },
  purchaseDebitNotes: { label: "Purchase Debit Notes", crumb: "Purchases" },
  salesOrders: { label: "Sales Orders", crumb: "Sales" },
  salesInvoices: { label: "Sales Invoices", crumb: "Sales" },
  schemes: { label: "Schemes", crumb: "Sales" },
  creditDebitNotes: { label: "Credit / Debit Notes", crumb: "Sales" },
  paymentReceipts: { label: "Payment Receipts", crumb: "Sales" },
  cashDeposits: { label: "Cash Deposits", crumb: "Sales" },
  stockBalances: { label: "Stock Balances", crumb: "Inventory" },
  stockLedger: { label: "Stock Ledger", crumb: "Inventory" },
  warehouses: { label: "Warehouses", crumb: "Inventory" },
  loadingSheets: { label: "Loading Sheets", crumb: "Delivery" },
  users: { label: "Users", crumb: "Admin" },
  distributors: { label: "Distributors", crumb: "Admin" },
  roles: { label: "Roles & Permissions", crumb: "Admin" },
  importColumnMapping: { label: "Import Column Mapping", crumb: "Admin" },
  voucherTypes: { label: "Voucher Types", crumb: "Admin" },
  ledgers: { label: "Ledgers", crumb: "Accounting" },
  accountingVouchers: { label: "Vouchers / Day Book", crumb: "Accounting" },
  accountingSettings: { label: "Accounting Settings", crumb: "Accounting" },
  financialSummaryReport: { label: "Financial Summary", crumb: "Accounting Reports" },
  balanceSheetReport: { label: "Balance Sheet", crumb: "Accounting Reports" },
  ledgerWiseTotalsReport: { label: "Ledger-wise Totals (Trial Balance)", crumb: "Accounting Reports" },
  ledgerVouchersReport: { label: "Ledger Vouchers", crumb: "Accounting Reports" },
  cashBankReport: { label: "Cash & Bank Balances", crumb: "Accounting Reports" },
  cashFlowReport: { label: "Cash Flow (Inflow / Outflow)", crumb: "Accounting Reports" },
  workingCapitalReport: { label: "Current Assets & Liabilities", crumb: "Accounting Reports" },
  ratioAnalysisReport: { label: "Ratio Analysis", crumb: "Accounting Reports" },
  stockValuationReport: { label: "Stock Valuation", crumb: "Accounting Reports" },
  receivablesReport: { label: "Receivables & Overdue", crumb: "Accounting Reports" },
  billWisePaymentsReport: { label: "Bill Wise Payment Tracker", crumb: "Accounting Reports" },
  outstandingBillsReport: { label: "Outstanding / Pending Bills", crumb: "Accounting Reports" },
  payablesReport: { label: "Payables & Overdue", crumb: "Accounting Reports" },
  stockSummaryReport: { label: "Stock Summary", crumb: "Accounting Reports" },
  closingStockReport: { label: "Closing Stock", crumb: "Accounting Reports" },
  categoryClosingStockReport: { label: "Category Wise Closing Stock", crumb: "Accounting Reports" },
  stockMovementReport: { label: "Stock Movement", crumb: "Accounting Reports" },
  salesRegisterReport: { label: "Sales Register", crumb: "Accounting Reports" },
  purchaseRegisterReport: { label: "Purchase Register", crumb: "Accounting Reports" },
  gstReport: { label: "GST Reports", crumb: "Accounting Reports" },
  itemWiseSalesReport: { label: "Product Wise Sales", crumb: "Accounting Reports" },
  creditNotesReport: { label: "Credit Notes", crumb: "Accounting Reports" },
  customerWiseSalesReport: { label: "Customer-wise Sales", crumb: "Accounting Reports" },
  salesmanWiseSalesReport: { label: "Salesman-wise Sales", crumb: "Accounting Reports" },
  profitLossReport: { label: "Profit & Loss", crumb: "Accounting Reports" },
  expenseReport: { label: "Expense Report", crumb: "Accounting Reports" },
  ledgerStatementReport: { label: "Customer Ledger Statement", crumb: "Accounting Reports" },
  cashInHandReport: { label: "Cash in Hand", crumb: "Accounting Reports" },
  profile: { label: "Company Profile", crumb: "" },
  ...receivablesFeatureMeta,
};

const getInitials = (email) => {
  if (!email) return "?";
  const namePart = email.split("@")[0];
  const pieces = namePart.split(/[._-]/).filter(Boolean);
  const initials = pieces.slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
  return initials || namePart.slice(0, 2).toUpperCase();
};

const Dashboard = ({ email, onLogout }) => {
  const [activePage, setActivePage] = useState("overview");
  const [paletteOpen, setPaletteOpen] = useState(false);
  // bumped when alerts are suppressed on the Alerts page, so the scrolling bar reloads
  const [alertsVersion, setAlertsVersion] = useState(0);
  // multi-company view: the pages are remounted when the super admin picks another company, so they reload its data
  const [viewCompanyId, setViewCompanyId] = useState(() => getViewCompanyId());

  const renderContent = () => {
    if (isReceivablePage(activePage)) return renderReceivablePage(activePage);
    switch (activePage) {
      case "overview":
        return <Overview onNavigate={setActivePage} />;
      case "alerts":
        return <Alerts onChanged={() => setAlertsVersion((v) => v + 1)} />;
      case "stockTransfers":
        return <StockTransfers />;
      case "stockJournalReport":
        return <StockJournalReport />;
      case "stockLowReport":
        return <ReportTable key="stockLow" {...stockLowConfig} rowStyle={(row) => (Number(row.currentStock) <= 0 ? { background: "#fee2e2" } : undefined)} />;
      case "stockExcessReport":
        return <ReportTable key="stockExcess" {...stockExcessConfig} />;
      case "closingStockHistory":
        return <ClosingStockHistory />;
      case "productWiseReturnsReport":
        return <ReportTable key="productWiseReturns" {...productWiseReturnsConfig} />;
      case "schemeDiscountReport":
        return <ReportTable key="schemeDiscount" {...schemeDiscountConfig} />;
      case "importHistory":
        return <ImportHistory />;
      case "distributorLinks":
        return <DistributorLinks />;
      case "brands":
        return <Brands />;
      case "banks":
        return <Banks />;
      case "products":
        return <Products />;
      case "taxRates":
        return <TaxRates />;
      case "productCategories":
        return <ProductCategories />;
      case "priceLists":
        return <PriceLists />;
      case "customers":
        return <Customers />;
      case "beats":
        return <Beats />;
      case "salesmanBeats":
        return <SalesmanBeats />;
      case "headerOnlyInvoices":
        return <HeaderOnlyInvoiceImport />;
      case "customerBeats":
        return <CustomerBeats />;
      case "purchaseInvoices":
        return <PurchaseInvoices />;
      case "purchaseDebitNotes":
        return <PurchaseDebitNotes />;
      case "salesOrders":
        return <SalesOrders />;
      case "salesInvoices":
        return <SalesInvoices />;
      case "schemes":
        return <Schemes />;
      case "creditDebitNotes":
        return <CreditDebitNotes />;
      case "paymentReceipts":
        return <PaymentReceipts />;
      case "cashDeposits":
        return <CashDeposits />;
      case "stockBalances":
        return <StockBalances />;
      case "stockLedger":
        return <StockLedger />;
      case "warehouses":
        return <Warehouses />;
      case "loadingSheets":
        return <LoadingSheets />;
      case "users":
        return <Users onNavigate={setActivePage} />;
      case "distributors":
        return <Distributors />;
      case "roles":
        return <Roles />;
      case "firms":
        return <Firms />;
      case "importColumnMapping":
        return <ImportColumnMapping />;
      case "voucherTypes":
        return <VoucherTypes />;
      case "ledgers":
        return <Ledgers />;
      case "accountingVouchers":
        return <Vouchers />;
      case "accountingSettings":
        return <AccountingSettings />;
      case "financialSummaryReport":
        return <FinancialSummary onNavigate={setActivePage} />;
      case "balanceSheetReport":
        return <BalanceSheet />;
      case "ledgerWiseTotalsReport":
        return <LedgerWiseTotals />;
      case "ledgerVouchersReport":
        return <LedgerVouchers />;
      case "cashBankReport":
        return <CashBankBalances />;
      case "cashFlowReport":
        return <CashFlow />;
      case "workingCapitalReport":
        return <WorkingCapital />;
      case "ratioAnalysisReport":
        return <RatioAnalysis />;
      case "stockValuationReport":
        return <StockValuation />;
      case "receivablesReport":
        return <OutstandingAgeing key="receivable" kind="receivable" />;
      case "billWisePaymentsReport":
        return <ReportTable key="billWisePayments" {...billWisePaymentsConfig} />;
      case "outstandingBillsReport":
        return <OutstandingBillsReport />;
      case "payablesReport":
        return <OutstandingAgeing key="payable" kind="payable" />;
      case "stockSummaryReport":
        return <ReportTable key="stockSummary" {...stockSummaryConfig} />;
      case "closingStockReport":
        return <ClosingStockReport />;
      case "categoryClosingStockReport":
        return <CategoryClosingStockReport />;
      case "stockMovementReport":
        return <ReportTable key="stockMovement" {...stockMovementConfig} />;
      case "salesRegisterReport":
        return <ReportTable key="salesRegister" {...salesRegisterConfig} />;
      case "purchaseRegisterReport":
        return <ReportTable key="purchaseRegister" {...purchaseRegisterConfig} />;
      case "gstReport":
        return <GstReport />;
      case "itemWiseSalesReport":
        return <ItemWiseSalesReport />;
      case "creditNotesReport":
        return <ReportTable key="creditNotes" {...creditNotesConfig} />;
      case "customerWiseSalesReport":
        return <ReportTable key="customerWiseSales" {...customerWiseSalesConfig} />;
      case "salesmanWiseSalesReport":
        return <ReportTable key="salesmanWiseSales" {...salesmanWiseSalesConfig} />;
      case "expenseReport":
        return <ExpenseReport />;
      case "profitLossReport":
        return <ProfitAndLoss />;
      case "ledgerStatementReport":
        return <LedgerStatementReport />;
      case "cashInHandReport":
        return <CashInHandReport />;
      case "profile":
        return <DistributorProfile mode="edit" />;
      default:
        return <div>Select an option</div>;
    }
  };

  const meta = featureMeta[activePage] || { label: "Dashboard", crumb: "" };

  return (
    <div className="flex">
      <Sidebar activePage={activePage} setActivePage={setActivePage} />
      <main className="ml-64 w-full min-h-screen" style={{ minWidth: 0, overflowX: "hidden", padding: 5 }}>
        <div className="header-bar">
          <div className="header-left">
            {meta.crumb ? <div className="header-breadcrumb">{meta.crumb}</div> : null}
            <h2>{meta.label}</h2>
          </div>
          <div className="header-right">
            <CompanySwitcher onChange={setViewCompanyId} />
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              title="Search any menu option (Ctrl/Cmd+K)"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "var(--color-surface-alt, #f3f4f6)",
                border: "1px solid var(--color-border)",
                borderRadius: "8px",
                padding: "6px 12px",
                cursor: "pointer",
                color: "var(--color-text-faint)",
                fontSize: "13px",
              }}
            >
              <Search size={14} />
              <span>Search menu...</span>
              <kbd style={{ fontSize: "11px", border: "1px solid var(--color-border)", borderRadius: "4px", padding: "1px 5px" }}>
                Ctrl K
              </kbd>
            </button>
            <button
              type="button"
              className="user-chip"
              onClick={() => setActivePage("profile")}
              title="Company profile"
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
            >
              <div className="user-avatar">{getInitials(email)}</div>
              <p className="user-email">{email}</p>
            </button>
            <button className="logout" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
        <AlertsMarquee key={`${viewCompanyId || "own"}-${alertsVersion}`} onOpen={() => setActivePage("alerts")} />
        {activePage === "overview" ? (
          <Fragment key={viewCompanyId || "own"}>{renderContent()}</Fragment>
        ) : (
          <div key={viewCompanyId || "own"} className="page-panel">
            {renderContent()}
          </div>
        )}
      </main>
      <CommandPalette setActivePage={setActivePage} open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
};

export default Dashboard;
